import React from 'react';
import PropTypes from 'prop-types';
import View from "react-native-web/dist/exports/View";
import { a11yProps, getA11yPropsFromHtmlTag, getTokensPropType, layoutTags, responsiveProps, selectSystemProps, spacingProps, useSpacingScale, useResponsiveProp, viewProps } from '../utils';
import { useThemeTokens } from '../ThemeProvider';
import { useViewport } from '../ViewportProvider';
import getStackedContent from './getStackedContent';
import { staticStyles, selectFlexStyles } from './common';
import { jsx as _jsx } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([a11yProps, viewProps]);
const offsetSides = {
  row: 'marginBottom',
  column: 'marginRight'
};
const gapSides = {
  row: 'bottom',
  column: 'right'
};
const spaceSides = {
  row: 'right',
  column: 'bottom'
};

/**
 * An alternative fallback implementation of `StackWrap` that doesn't use CSS `gap`.
 *
 * This is not intended to be used directly. It will automatically be used instead of StackWrap in:
 *
 * - Native apps (no `gap` style prop in React Native).
 * - Older (~<=2020) browsers which don't support CSS `gap`.
 * - Cases where the `space` between items should be different to the `gap` between wrapped rows .
 *
 * Unlike the cleaner and more side-effect-free CSS gap StackWrap implementation, this renders a Box (View)
 * between the container and the stacked children, and requires a negative margin on the container.
 */
const StackWrapBox = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    space = 1,
    gap = space,
    direction: directionProp = 'row',
    children,
    tokens,
    variant,
    tag,
    accessibilityRole,
    ...rest
  } = _ref;
  const viewport = useViewport();
  const direction = useResponsiveProp(directionProp, 'row');
  const themeTokens = useThemeTokens('StackView', tokens, variant, {
    viewport
  });
  const flexStyles = selectFlexStyles(themeTokens);
  const selectedProps = selectProps({
    accessibilityRole,
    ...getA11yPropsFromHtmlTag(tag, accessibilityRole),
    ...rest
  });

  // Mimic CSS `gap` using box spacing on the side after a wrapped row then offsetting it on the last row.
  const gapSize = useSpacingScale(gap);
  const offsetStyle = {
    [offsetSides[direction]]: -1 * gapSize
  };
  const boxProps = {
    [gapSides[direction]]: gap,
    [spaceSides[direction]]: space
  };
  const content = getStackedContent(children, {
    direction,
    space: 0,
    box: boxProps
  });
  return /*#__PURE__*/_jsx(View, {
    ref: ref,
    ...selectedProps,
    style: [flexStyles, staticStyles.wrap, staticStyles[direction], offsetStyle],
    children: content
  });
});
StackWrapBox.displayName = 'StackWrapBox';
StackWrapBox.propTypes = {
  ...selectedSystemPropTypes,
  tokens: getTokensPropType('StackView'),
  /**
   * Sets the `flexDirection` of the container
   */
  direction: responsiveProps.getTypeOptionallyByViewport(PropTypes.oneOf(['column', 'row', 'row-reverse', 'column-reverse'])),
  /**
   * The size of the space between items according to the theme's spacing scale.
   * Either a number corresponding to a position on the theme's spacing scale (1 is smallest, 2 is second smallest, etc),
   * or, a SpacingObject with viewport keys and options (see `useSpacingScale` for details).
   */
  space: spacingProps.types.spacingValue,
  /**
   * The size of the space between lines of content, according to the theme's spacing scale. By default, matches `space`.
   * Either a number corresponding to a position on the theme's spacing scale (1 is smallest, 2 is second smallest, etc),
   * or, a SpacingObject with viewport keys and options (see `useSpacingScale` for details).
   */
  gap: spacingProps.types.spacingValue,
  /**
   * Optional semantic HTML tag, to apply to the container on web. Does not change styling.
   *
   * In native apps, if a header tag is provided ("h1", "h2" etc) and accessibilityRole prop
   * is not defined, the accessibilityRole will default to "heading".
   */
  tag: PropTypes.oneOf(layoutTags),
  /**
   * A StackWrap may take any children, but will have no effect if it is only passed one child or is passed children
   * wrapped in a component. If necessary, children may be wrapped in one React Fragment.
   */
  children: PropTypes.node
};
export default StackWrapBox;